import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getToken } from "../lib/session";  // Assuming this is your token fetching utility

const API_BASE = process.env.REACT_APP_API || "http://localhost:4500/api";  // API Base URL

export default function BrandDetail() {
  const { id } = useParams();  // Get the brand id from the URL
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch the brand details based on the brand ID
  useEffect(() => {
    async function fetchBrand() {
      try {
        const token = getToken();
        const response = await fetch(`${API_BASE}/brands/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,  // Send the token for authorization
          },
        });
        if (!response.ok) throw new Error("Brand not found");
        const data = await response.json();
        setBrand(data);
      } catch (err) {
        setError("Failed to load brand data");
      } finally {
        setLoading(false);
      }
    }
    fetchBrand();
  }, [id]);

  // Handle saving the brand details after updating
  const handleSave = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/brands/${id}`, {
        method: "PUT",  // Use PUT to update the brand data
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(brand),  // Send updated brand data
      });

      if (!response.ok) {
        const errorText = await response.text();
        setError(`Failed to save brand data: ${errorText}`);
      } else {
        navigate("/dashboard?tab=brands");  // Redirect to the brands list on success
      }
    } catch (err) {
      setError("Failed to save brand data");
    }
  };

  // Loading state or error display
  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <h2 className="text-3xl font-extrabold text-center mb-6">Edit Brand</h2>
        
        {/* Brand Logo Display */}
        {brand.logoUrl && (
          <div className="text-center mb-6">
            <img src={brand.logoUrl} alt="Brand Logo" className="w-32 h-32 mx-auto object-contain" />
          </div>
        )}

        <div className="space-y-6">
          {/* Business Name */}
          <div>
            <label htmlFor="name" className="block text-lg font-medium text-slate-700">Brand/Business Name</label>
            <input
              type="text"
              id="businessName"
              value={brand.name || ""}
              onChange={(e) => setBrand({ ...brand, name: e.target.value })}
              placeholder="Enter Business Name"
              className="mt-2 block w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Website */}
          <div>
            <label htmlFor="website" className="block text-lg font-medium text-slate-700">Website</label>
            <input
              type="text"
              id="website"
              value={brand.website || ""}
              onChange={(e) => setBrand({ ...brand, website: e.target.value })}
              placeholder="Enter Website URL"
              className="mt-2 block w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-lg font-medium text-slate-700">Description</label>
            <textarea
              id="description"
              value={brand.description || ""}
              onChange={(e) => setBrand({ ...brand, description: e.target.value })}
              placeholder="Enter Brand Description"
              rows="4"
              className="mt-2 block w-full px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Primary and Secondary Colors (using color plates) */}
          <div className="space-y-4">
            <div>
              <label htmlFor="primaryColor" className="block text-lg font-medium text-slate-700">Primary Color</label>
              <div
                className="mt-2 w-full h-10 rounded-md"
                style={{ backgroundColor: brand.primaryColor }}
              />
              <input
                type="color"
                id="primaryColor"
                value={brand.primaryColor || "#000000"}
                onChange={(e) => setBrand({ ...brand, primaryColor: e.target.value })}
                className="mt-2 block w-full p-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label htmlFor="secondaryColor" className="block text-lg font-medium text-slate-700">Secondary Color</label>
              <div
                className="mt-2 w-full h-10 rounded-md"
                style={{ backgroundColor: brand.secondaryColor }}
              />
              <input
                type="color"
                id="secondaryColor"
                value={brand.secondaryColor || "#000000"}
                onChange={(e) => setBrand({ ...brand, secondaryColor: e.target.value })}
                className="mt-2 block w-full p-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* Favicon (optional) */}
          {brand.metadata.favicon && (
            <div className="text-center mt-4">
              <h3 className="text-lg font-medium text-slate-700">Favicon</h3>
              <img src={brand.metadata.favicon} alt="Favicon" className="w-12 h-12 mx-auto object-contain mt-2" />
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end mt-6">
            <button
              onClick={handleSave}
              className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700"
            >
              Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
